import { useState } from 'react';
import { Button, theme } from 'antd';
import type { AuthClient } from '@titularis/api-client';
import { EtiquetaDaUltimaVez } from './EtiquetaDaUltimaVez';
import { lembrarFormaDeEntrada } from './formaDeEntrada';
import { MENSAGEM_DE_FALHA_INESPERADA, mensagemDeErro } from './mensagensDeAutenticacao';

/**
 * O "G" oficial do Google.
 *
 * Desenhado aqui, e não importado de um pacote de ícones: as diretrizes de
 * marca do Google exigem o símbolo em quatro cores, e a família de ícones que o
 * produto já usa (`@ant-design/icons`) só tem o `GoogleOutlined`, que é um
 * contorno monocromático - proibido no botão de entrar.
 *
 * Fica em 18px porque é a medida das diretrizes para o botão de altura padrão,
 * e não acompanha o tamanho do botão: o símbolo é marca de terceiro, não
 * elemento tipográfico nosso.
 */
function SimboloDoGoogle() {
  return (
    <svg viewBox="0 0 18 18" width={18} height={18} aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

/**
 * "Continuar com Google": a porta curta da tela de entrada.
 *
 * **Continuar**, e não "entrar": o mesmo botão serve quem já tem conta e quem
 * não tem, porque o BetterAuth cria a conta na volta se ela não existir. É o
 * que faz a tela atender o visitante que chegou do WhatsApp sem saber se já se
 * cadastrou - e é por isso que ele é o mesmo no login, no cadastro e no
 * backoffice.
 *
 * **No backoffice ele não cria acesso, e isso não é contradição.** A conta
 * nasce, o papel não: quem entrar sem papel administrativo é recusado pelo
 * `RequireAdmin`, com explicação. O botão resolve a autenticação; a autorização
 * continua sendo do servidor (G-POLICY).
 *
 * O clique **sai do site**: o BetterAuth responde com a URL do Google e o
 * navegador vai para lá. Por isso o estado de carregando não é desligado no
 * sucesso - a página inteira está indo embora, e apagar o spinner antes disso
 * só entregaria um botão que parece clicável enquanto nada acontece.
 */
export function ContinuarComGoogle({
  authClient,
  destino = '/',
  paginaDeEntrada = '/entrar',
  destaque = false,
  aoFalhar,
}: {
  /**
   * O cliente de autenticação do app que chama.
   *
   * Vem por propriedade, e não de um import: cada app cria o seu com a própria
   * `VITE_API_URL`, e um cliente criado aqui dentro não saberia de qual dos
   * dois é a URL. O tipo entra só como tipo - nada do `api-client` sobra no
   * pacote depois de compilado.
   */
  authClient: AuthClient;
  /** Caminho interno para onde voltar depois de autenticar. */
  destino?: string;
  /** A rota de entrada deste app, para onde a recusa do Google volta. */
  paginaDeEntrada?: string;
  /** Marca este botão como a forma usada na última vez. */
  destaque?: boolean;
  aoFalhar: (mensagem: string) => void;
}) {
  const { token } = theme.useToken();
  const [indo, setIndo] = useState(false);

  const continuar = async () => {
    aoFalhar('');
    setIndo(true);
    lembrarFormaDeEntrada('google');

    try {
      /*
       * As duas URLs são absolutas porque quem redireciona é a API, em outro
       * domínio: um caminho relativo cairia em `api.titularis.com.br/estudar`.
       * O `errorCallbackURL` traz a pessoa de volta para cá com `?error=...`,
       * e é a única forma de saber o que houve - a recusa acontece dentro do
       * fluxo de redirecionamento, longe desta chamada.
       */
      const { error } = await authClient.signIn.social({
        provider: 'google',
        callbackURL: new URL(destino, window.location.origin).toString(),
        errorCallbackURL: new URL(
          `${paginaDeEntrada}${destino === '/' ? '' : `?destino=${encodeURIComponent(destino)}`}`,
          window.location.origin,
        ).toString(),
      });

      if (error) {
        aoFalhar(mensagemDeErro(error, 'Não foi possível continuar com o Google.'));
        setIndo(false);
      }
    } catch {
      aoFalhar(MENSAGEM_DE_FALHA_INESPERADA);
      setIndo(false);
    }
  };

  return (
    <Button size="large" block loading={indo} onClick={() => void continuar()}>
      {/*
        Com etiqueta, a linha ocupa a largura toda e o rótulo vai para a
        esquerda; sem ela, o conteúdo fica centralizado como em qualquer botão.

        A diferença não é gosto: centralizados, símbolo, rótulo e etiqueta se
        atropelavam em tela estreita. Medido: com etiqueta, os três cabem a
        partir de 328px de botão - o celular de 360px, que hoje é o menor em
        uso. Abaixo disso quem cede é o rótulo, que reticencia, porque o
        `flexShrink: 0` protege símbolo e etiqueta: das três peças, a única que
        continua dizendo o que é pela metade é a palavra.
      */}
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: token.marginXS,
          width: '100%',
          justifyContent: destaque ? 'flex-start' : 'center',
        }}
      >
        <span style={{ display: 'flex', flexShrink: 0 }}>
          <SimboloDoGoogle />
        </span>

        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Continuar com Google
        </span>

        {destaque ? (
          <span style={{ marginInlineStart: 'auto', display: 'flex', flexShrink: 0 }}>
            <EtiquetaDaUltimaVez />
          </span>
        ) : null}
      </span>
    </Button>
  );
}
